import "./instrument";
import React, { Suspense } from "react";
import { createRoot } from "react-dom/client";
import { createTheme, MantineProvider } from "@mantine/core";
import { emotionTransform, MantineEmotionProvider } from "@mantine/emotion";
import { MatomoProvider, createInstance } from "@datapunt/matomo-tracker-react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Notifications } from "@mantine/notifications";
import { App } from "./App";
import { LoadingScreen } from "./LoadingScreen";
import { ErrorBoundary } from "./ErrorBoundary";
import { SharedLocationRedirect } from "./SharedLocationRedirect";
import "@mantine/core/styles.css";
import "@mantine/notifications/styles.css";
import "./index.css";
import "./utils/i18n/i18n";

const BASE_API_URL = import.meta.env.VITE_BASE_API_URL;

const matomoInstance = createInstance({
  urlBase: BASE_API_URL,
  siteId: 3,
  trackerUrl: "https://analytics.vecturagames.com/matomo.php",
  srcUrl: "https://analytics.vecturagames.com/matomo.js",
  linkTracking: false,
  configurations: {
    disableCookies: true,
  },
});

const trackbookTheme = createTheme({
  fontSizes: {
    xs: "13px",
  },
  shadows: {
    xs: "0 1px 2px 0 rgb(0 0 0 / 0.1)",
    sm: "0 1px 3px 0 rgb(0 0 0 / 0.2), 0 1px 2px -1px rgb(0 0 0 / 0.2)",
    md: "0 4px 6px -1px rgb(0 0 0 / 0.2), 0 2px 4px -2px rgb(0 0 0 / 0.2)",
    lg: "0 10px 15px -3px rgb(0 0 0 / 0.2), 0 4px 6px -4px rgb(0 0 0 / 0.2)",
    xl: "0 20px 25px -5px rgb(0 0 0 / 0.2), 0 8px 10px -6px rgb(0 0 0 / 0.2)",
  },
  other: {
    // shadow used for map controls
    controlButtonShadow:
      "0px 1px 4px rgb(0 0 0 / 20%), 0 1px 2px rgb(0 0 0 / 20%)",
  },
  defaultRadius: "sm",
  cursorType: "pointer",
  primaryColor: "violet",
});

const container = document.getElementById("root");
const root = createRoot(container!);

root.render(
  <React.StrictMode>
    <MatomoProvider value={matomoInstance}>
      <MantineProvider
        defaultColorScheme="auto"
        theme={trackbookTheme}
        stylesTransform={emotionTransform}
      >
        <MantineEmotionProvider>
          <Suspense fallback={<LoadingScreen />}>
            <BrowserRouter>
              <Routes>
                <Route
                  path="/"
                  element={
                    <ErrorBoundary>
                      <Notifications />
                      <App />
                    </ErrorBoundary>
                  }
                />
                <Route path="/loc" element={<SharedLocationRedirect />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
          </Suspense>
        </MantineEmotionProvider>
      </MantineProvider>
    </MatomoProvider>
  </React.StrictMode>
);
